import { useEffect, useMemo, useState } from "react";
import { addressCategories, addressLines } from "@/mocks/home";

async function copyText(text: string) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "absolute";
    area.style.left = "-9999px";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}

const toneMap: Record<string, string> = {
  accent: "bg-accent-100 text-accent-900 border border-accent-200",
  secondary: "bg-secondary-100 text-secondary-900 border border-secondary-200",
  primary: "bg-primary-100 text-primary-800 border border-primary-200",
};

export default function AddressSection() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const filtered = useMemo(
    () =>
      activeCategory === "all"
        ? addressLines
        : addressLines.filter((line) => line.category === activeCategory),
    [activeCategory]
  );

  useEffect(() => {
    if (!copiedId) return undefined;
    const timer = window.setTimeout(() => setCopiedId(null), 2200);
    return () => window.clearTimeout(timer);
  }, [copiedId]);

  const handleCopy = async (id: string, address: string) => {
    const ok = await copyText(address);
    if (ok) {
      setCopiedId(id);
    }
  };

  return (
    <section id="addresses" className="w-full px-4 md:px-8 py-16 md:py-24 bg-background-50">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-800 text-xs md:text-sm">
              <i className="ri-link-m" />
              最新可用地址
            </span>
            <h2 className="mt-4 font-heading font-bold text-2xl md:text-4xl text-foreground-950">
              当前同步的可用线路
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              下面这些是最近一次同步的可访问入口。请优先打开标为
              <strong className="font-medium text-primary-700">「主线路」</strong>
              的一条；如果它暂时打不开，再按顺序尝试备用线路与镜像入口，多数情况很快就能连通。
            </p>
          </div>
          <p className="text-xs md:text-sm text-foreground-600 max-w-xs lg:text-right">
            列表中的地址为演示用的示例格式，实际请以本页每次同步后展示的入口为准。
          </p>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-2">
          {addressCategories.map((category) => {
            const isActive = activeCategory === category.key;
            return (
              <button
                key={category.key}
                type="button"
                onClick={() => setActiveCategory(category.key)}
                aria-pressed={isActive}
                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                  isActive
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 border border-background-200 hover:bg-background-200"
                }`}
              >
                {category.label}
              </button>
            );
          })}
          <span className="text-xs md:text-sm text-foreground-600 ml-1">
            共 {filtered.length} 条
          </span>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
          {filtered.map((line) => (
            <article
              key={line.id}
              className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 hover:border-primary-300 transition-colors"
            >
              <div className="flex items-center justify-between gap-2">
                <span className={`px-3 py-1 rounded-full text-xs whitespace-nowrap ${toneMap[line.tagTone]}`}>
                  {line.tag}
                </span>
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs whitespace-nowrap ${toneMap[line.statusTone]}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-current" />
                  {line.statusLabel}
                </span>
              </div>

              <h3 className="mt-4 font-heading font-bold text-lg text-foreground-950">{line.name}</h3>
              <p className="mt-2 text-sm text-foreground-700 leading-relaxed flex-1">{line.desc}</p>

              <div className="mt-4 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2">
                <i className="ri-global-line text-primary-600 text-base" />
                <span className="flex-1 min-w-0 truncate font-mono text-xs md:text-sm text-foreground-800">
                  {line.address}
                </span>
                <button
                  type="button"
                  onClick={() => handleCopy(line.id, line.address)}
                  aria-label={`复制 ${line.name} 的地址`}
                  className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-background-50 border border-background-300 text-xs text-foreground-700 whitespace-nowrap hover:bg-primary-50 hover:text-primary-700 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <i className={`${copiedId === line.id ? "ri-check-line" : "ri-file-copy-line"} text-sm`} />
                  {copiedId === line.id ? "已复制" : "复制"}
                </button>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-600">
                <span className="inline-flex items-center gap-1">
                  <i className="ri-time-line" />
                  更新于 {line.updatedAt}
                </span>
                <span className="inline-flex items-center gap-1">
                  <i className="ri-speed-up-line" />
                  {line.latency}
                </span>
              </div>
            </article>
          ))}
        </div>
      </div>

      {copiedId && (
        <div className="fixed left-1/2 bottom-6 -translate-x-1/2 z-[60] px-5 py-3 rounded-lg bg-foreground-950 text-background-50 text-sm flex items-center gap-2 max-w-[90vw]">
          <i className="ri-checkbox-circle-line text-accent-400 text-lg" />
          地址已复制到剪贴板，粘贴到浏览器地址栏即可打开
        </div>
      )}
    </section>
  );
}